/**
 * SoundExpertConfigTab — Config tab content for the SoundExpert node.
 *
 * Layout:
 *   - Top tabs: Speech / Music / SFX mode selection
 *   - Main area: mode-specific prompt controls
 *   - Speech mode: inline voice catalogue, not a separate flyover button
 *   - Bottom toolbar: mode settings where needed, retry count, sparks, Generate
 *
 * Speech mode uses RichTextBox with @-mention support.
 * Music/SFX use plain text prompts.
 */

import { memo, useCallback, useEffect, useRef, useState, type WheelEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Mic2, Music, Sparkles, type LucideIcon } from 'lucide-react'
import { CanvasRichTextBox, type RichTextBoxState } from './RichTextBox'
import { SoundConfigFlyover, SoundExpertSpeechVoicePanel } from './SoundConfigFlyover'
import { FlyoutPortal } from './FlyoutPortal'
import { CANVAS_LOCAL_WHEEL_TARGET_ATTR } from '../../canvasWheelScope'
import {
  audioSparksPerGeneration,
  SFX_PROMPT_POLICY,
  SPEECH_TEXT_POLICY,
  type SoundExpertNodeData,
  type VoiceProfileDict,
  type VoiceProfileDictYMap,
} from '../../../../generated/nodeDataTypes'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { useNuxTooltipEvents } from '../../../../onboarding/nuxEvents'

// Dev-gated (VITE_NAD_DEBUG) trace of every sound-mode switch. The key field is
// `speechContentLength`: switching to speech must leave it exactly as the user
// left it, never bumped to the length of an injected template.
const modeLog = createNadDebug('dw:sound-expert:mode-switch')

// Dev-gated (VITE_NAD_DEBUG) trace of the speech editor's measured layout. This
// bug class is invisible to unit tests and to the DOM tree alike — both the
// broken and fixed builds render the same elements. The only signal is measured
// height: `editorHeight` used to sit at the 120px cap while `availableHeight`
// stretched to the panel, leaving `unusedHeight` of dead space under the text
// (post 1597931935297437). `fillsContainer` is the assertion in one field.
const layoutLog = createNadDebug('dw:sound-expert:speech-box-layout')

// Dev-gated (VITE_NAD_DEBUG) trace of the speech editor's character counter.
// The speech box used to inherit RichTextBox's default maxLength of 1000, so a
// 1163-char script that rendered fine showed a red "1163 / 1000" (post
// 1639600894463874). The only counter now is SPEECH_TEXT_POLICY's (eleven_v3's
// 5000): `richTextCounterVisible` must be false, and `speechCounterState` must
// match the script length against `maxChars` / `warnChars`.
const speechCounterLog = createNadDebug('dw:sound-expert:speech-char-counter')
const WHEEL_ISOLATION_LOG_NAMESPACE = 'dw:sound-expert:wheel-isolation'
const wheelIsolationLog = createNadDebug(WHEEL_ISOLATION_LOG_NAMESPACE)

// --- Types ------------------------------------------------------------------

export type SoundMode = NonNullable<SoundExpertNodeData['in_mode']>

export interface SoundExpertConfigTabProps {
  /** Stable ID of the canvas node that owns this prompt editor. */
  nodeId: string
  mode: SoundMode
  musicPrompt: string
  sfxPrompt: string
  speechContent: string
  duration: string
  loop: string
  lyricsMode: string
  model: string
  voiceProfile?: VoiceProfileDict | VoiceProfileDictYMap
  allowBlankSpeechWithVoiceProfile?: boolean
  isRunning: boolean
  retryCount: number
  onModeChange: (mode: SoundMode) => void
  onMusicPromptChange: (value: string) => void
  onSfxPromptChange: (value: string) => void
  onSpeechContentChange: (state: RichTextBoxState) => void
  onDurationChange: (value: string) => void
  onLoopChange: (value: string) => void
  onLyricsModeChange: (value: string) => void
  onModelChange: (value: string) => void
  onVoiceProfileChange?: (profile: VoiceProfileDict | undefined) => void
  onRetryCountChange: (count: number) => void
  onGenerate: () => void
  onMentionClick?: (nodeId: string) => void
}

// --- Constants --------------------------------------------------------------

const MODES: SoundMode[] = ['music', 'speech', 'sfx']

const MODE_ICONS: Record<SoundMode, LucideIcon> = {
  music: Music,
  speech: Mic2,
  sfx: Sparkles,
}

const MODE_LABEL_KEYS: Record<SoundMode, string> = {
  music: 'soundExpertConfigTab.modeLabelMusic',
  speech: 'soundExpertConfigTab.modeLabelSpeech',
  sfx: 'soundExpertConfigTab.modeLabelSfx',
}

const PROMPT_PLACEHOLDER_KEYS: Record<SoundMode, string> = {
  music: 'soundExpertConfigTab.placeholderMusic',
  speech: '',
  sfx: 'soundExpertConfigTab.placeholderSfx',
}

/**
 * Backend pricing key for sound_expert (NODE_PRICING['sound_expert'] ->
 * 'elevenlabs', per_generation $0.30 -> 6 sparks). Kept as a const so the
 * generated mirror lookup is explicit and the cross-language parity test can
 * pin it.
 */
const SOUND_EXPERT_PRICING_MODEL = 'elevenlabs'
const LOCAL_WHEEL_TARGET_PROPS = {
  [CANVAS_LOCAL_WHEEL_TARGET_ATTR]: 'true',
  'data-canvas-local-wheel-source-feature': 'sound-expert',
}

const ACCENT = {
  base: '#06b6d4',
  bg20: 'rgba(6, 182, 212, 0.2)',
  bg40: 'rgba(6, 182, 212, 0.4)',
  bg90: 'rgba(6, 182, 212, 0.9)',
}

// --- Config Summary Helper --------------------------------------------------

function getConfigSummary(
  mode: SoundMode,
  duration: string,
  loop: string,
  lyricsMode: string,
  t: (key: string) => string,
): string {
  if (mode === 'sfx') {
    const durLabel = duration === 'auto' ? t('soundExpertConfigTab.configAuto') : duration
    const loopLabel = loop === 'on' ? t('soundExpertConfigTab.configOn') : t('soundExpertConfigTab.configOff')
    return `${durLabel} · ${loopLabel}`
  }
  if (mode === 'music') {
    const durLabel = duration === 'auto' ? t('soundExpertConfigTab.configAuto') : duration
    const lyricsLabel = t(`soundExpertConfigTab.lyricsMode_${lyricsMode}`)
    return `${durLabel} · ${lyricsLabel}`
  }
  // speech
  return t('soundExpertConfigTab.configVoice')
}

function voiceProfileTextValue(
  voiceProfile: VoiceProfileDict | VoiceProfileDictYMap | undefined,
  key: 'description' | 'voice_prompt' | 'name',
): string {
  if (!voiceProfile) return ''
  const value = voiceProfile[key]
  return typeof value === 'string' ? value.trim() : ''
}

function hasVoiceBriefForGeneratedSample(
  voiceProfile: VoiceProfileDict | VoiceProfileDictYMap | undefined,
): boolean {
  return Boolean(
    voiceProfileTextValue(voiceProfile, 'description')
      || voiceProfileTextValue(voiceProfile, 'voice_prompt')
      || voiceProfileTextValue(voiceProfile, 'name'),
  )
}

// --- Component --------------------------------------------------------------

const SoundExpertConfigTab = memo(function SoundExpertConfigTab({
  nodeId,
  mode,
  musicPrompt,
  sfxPrompt,
  speechContent,
  duration,
  loop,
  lyricsMode,
  model: _model,
  voiceProfile,
  allowBlankSpeechWithVoiceProfile,
  isRunning,
  retryCount,
  onModeChange,
  onMusicPromptChange,
  onSfxPromptChange,
  onSpeechContentChange,
  onDurationChange,
  onLoopChange,
  onLyricsModeChange,
  onModelChange: _onModelChange,
  onVoiceProfileChange,
  onRetryCountChange,
  onGenerate,
  onMentionClick,
}: SoundExpertConfigTabProps) {
  const { t } = useTranslation('canvas')
  const { emitNuxEvent } = useNuxTooltipEvents()
  const promptAreaRef = useRef<HTMLDivElement>(null)
  const configChipRef = useRef<HTMLButtonElement>(null)
  const retryPillRef = useRef<HTMLButtonElement>(null)
  const [isFlyoverOpen, setIsFlyoverOpen] = useState(false)
  const [isRetryPopoverOpen, setIsRetryPopoverOpen] = useState(false)

  // Measure the speech editor against the space it is given. Runs on mode
  // switch and whenever the panel is resized, so a regression shows up as a
  // non-zero `unusedHeight` in `nad trace --feature dw:sound-expert:speech-box-layout`.
  //
  // The comparison MUST be against the editor's own parent content box, not the
  // outer prompt area. Three boxes nest here:
  //   promptAreaRef      (px-3 pt-3 pb-2)      <- outer, 20px vertical padding
  //   └─ speech-prompt-area (p-2 + 1px border) <- the editor's parent
  //      └─ rich-text-editor                   <- what we measure
  // Measuring against the outer box charges the editor for ~38px of padding it
  // can never occupy, so `unusedHeight` would never reach 0 and `fillsContainer`
  // would be permanently false — a monitoring signal hardcoded to cry wolf.
  useEffect(() => {
    if (mode !== 'speech') return
    // Read the rendered counters rather than echoing constants, so the trace
    // reports what the user actually sees.
    const container = promptAreaRef.current
    const richTextCounter = container?.querySelector('[data-testid="rich-text-char-counter"]')
    const speechCounter = container?.querySelector<HTMLElement>(
      '[data-testid="speech-text-counter"]',
    )
    speechCounterLog({
      event: 'speech-char-counter',
      trace_id: `sound-expert-speech-counter:${nodeId}`,
      nodeId,
      speechContentLength: speechContent.length,
      maxChars: SPEECH_TEXT_POLICY.maxChars,
      warnChars: SPEECH_TEXT_POLICY.warnChars,
      richTextCounterVisible: Boolean(richTextCounter),
      speechCounterState: speechCounter?.dataset.state ?? 'hidden',
    })
  }, [mode, nodeId, speechContent.length])

  useEffect(() => {
    if (mode !== 'speech') return
    const container = promptAreaRef.current
    if (!container) return

    const measure = () => {
      const box = container.querySelector<HTMLElement>('[data-testid="speech-prompt-area"]')
      const editor = container.querySelector<HTMLElement>('[data-testid="rich-text-editor"]')
      if (!box || !editor) return

      // clientHeight excludes border and scrollbar but still includes padding.
      // jsdom resolves no stylesheet, so padding can come back as '' — coerce to
      // 0 rather than poisoning the whole measurement with NaN.
      const style = getComputedStyle(box)
      const px = (value: string) => (Number.isFinite(parseFloat(value)) ? parseFloat(value) : 0)
      const availableHeight = Math.round(
        box.clientHeight - px(style.paddingTop) - px(style.paddingBottom),
      )
      const editorHeight = Math.round(editor.getBoundingClientRect().height)

      layoutLog({
        event: 'speech-box-measured',
        trace_id: 'sound-expert-speech-box-layout',
        // Content-box height of the editor's parent — the space the editor can
        // actually occupy.
        availableHeight,
        editorHeight,
        // Dead space under the text. Was ~availableHeight-120 before the fix.
        unusedHeight: Math.max(0, availableHeight - editorHeight),
        // The editor may still scroll internally; that is expected for very
        // long dialogue and is NOT the bug.
        editorScrollHeight: editor.scrollHeight,
        contentLength: speechContent.length,
        // 1px of slack for sub-pixel rounding only.
        fillsContainer: availableHeight - editorHeight <= 1,
      })
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [mode, speechContent])

  const closeFlyover = useCallback(() => setIsFlyoverOpen(false), [])

  const closeRetryPopover = useCallback(() => setIsRetryPopoverOpen(false), [])

  const logPromptWheelCapture = useCallback(
    (event: WheelEvent<HTMLElement>, surface: 'speech' | 'music' | 'sfx') => {
      if (!import.meta.env?.VITE_NAD_DEBUG && !createDebug.enabled(WHEEL_ISOLATION_LOG_NAMESPACE)) return
      const scrollTarget =
        surface === 'speech'
          ? event.currentTarget.querySelector<HTMLElement>('[data-testid="rich-text-editor"]')
          : event.currentTarget
      const scrollTopBefore = scrollTarget?.scrollTop ?? null
      const scrollHeight = scrollTarget?.scrollHeight ?? null
      const clientHeight = scrollTarget?.clientHeight ?? null
      const targetCanScroll = scrollHeight !== null && clientHeight !== null && scrollHeight > clientHeight
      const traceId = `sound-expert-wheel:${nodeId}`
      wheelIsolationLog({
        event: 'sound-expert-prompt-wheel-owned',
        trace_id: traceId,
        node_id: nodeId,
        mode,
        surface,
        target_role: surface === 'speech' ? 'rich-text-editor' : 'textarea',
        delta_y: event.deltaY,
        ctrl_key: event.ctrlKey,
        meta_key: event.metaKey,
        scroll_top_before: scrollTopBefore,
        scroll_height: scrollHeight,
        client_height: clientHeight,
        target_can_scroll: targetCanScroll,
        local_wheel_target: event.currentTarget.getAttribute(CANVAS_LOCAL_WHEEL_TARGET_ATTR) === 'true',
      })
      window.setTimeout(() => {
        const scrollTopAfter = scrollTarget?.scrollTop ?? null
        wheelIsolationLog({
          event: 'sound-expert-prompt-wheel-scroll-result',
          trace_id: traceId,
          node_id: nodeId,
          mode,
          surface,
          target_role: surface === 'speech' ? 'rich-text-editor' : 'textarea',
          scroll_top_before: scrollTopBefore,
          scroll_top_after: scrollTopAfter,
          scroll_delta_applied:
            scrollTopBefore !== null && scrollTopAfter !== null
              ? scrollTopAfter - scrollTopBefore
              : null,
        })
      }, 100)
    },
    [mode, nodeId],
  )

  // Switching to speech must NOT seed any text. A previous version pre-filled a
  // demo dialogue template here via `richTextRef.setText(...)` inside a
  // `setTimeout(..., 0)`. That was not a placeholder: it round-tripped through
  // `onSpeechContentChange` into `in_text` in YDoc, so the user ended up owning
  // 165 characters they never typed, with Generate already enabled. The speech
  // box now relies on the localized `writeDialogue` ghost text instead.
  const handleModeChange = useCallback(
    (newMode: SoundMode) => {
      modeLog({
        event: 'sound-mode-change',
        trace_id: 'sound-expert-mode-switch',
        from: mode,
        to: newMode,
        speechContentLength: speechContent.length,
        seededTemplate: false,
      })
      onModeChange(newMode)
      emitNuxEvent({
        type: 'controlActivated',
        controlId: newMode === 'speech'
          ? 'soundModeSpeech'
          : newMode === 'music'
            ? 'soundModeMusic'
            : 'soundModeSfx',
        anchorId: 'sound-node-purpose',
        nodeId,
        nodeType: 'soundExpert',
      })
      setIsFlyoverOpen(false)
    },
    [emitNuxEvent, mode, nodeId, onModeChange, speechContent],
  )

  const canGenerateBlankSpeechSample =
    allowBlankSpeechWithVoiceProfile ?? hasVoiceBriefForGeneratedSample(voiceProfile)

  const isPromptEmpty =
    mode === 'music'
      ? !musicPrompt.trim()
      : mode === 'sfx'
        ? !sfxPrompt.trim()
        : !speechContent.trim() && !canGenerateBlankSpeechSample

  const generateDisabled = isRunning || isPromptEmpty
  // Cost quote = per-generation sparks (from google_pricing.json via codegen)
  // x retry count. sound_expert -> 'elevenlabs' -> $0.30 -> 6 sparks, so this
  // matches what _deduct_sparks bills.
  const sparksCount = audioSparksPerGeneration(SOUND_EXPERT_PRICING_MODEL) * retryCount
  const configSummary = getConfigSummary(mode, duration, loop, lyricsMode, t)
  // Disclosure of the provider's declared SFX prompt ceiling. Stays hidden
  // until the limit is in reach so short prompts get no chrome at all.
  const sfxPromptLength = sfxPrompt.length
  const sfxPromptOverLimit = sfxPromptLength > SFX_PROMPT_POLICY.maxChars
  const sfxPromptNearLimit = sfxPromptLength >= SFX_PROMPT_POLICY.warnChars
  const textPromptMode = mode === 'music' ? 'music' : 'sfx'
  // Same soft disclosure for the speech script, against eleven_v3's 5000.
  // Counted from `speechContent` (the node's in_text), not RichTextBox's own
  // counter, which only counts typed input.
  const speechTextLength = speechContent.length
  const speechTextOverLimit = speechTextLength > SPEECH_TEXT_POLICY.maxChars
  const speechTextNearLimit = speechTextLength >= SPEECH_TEXT_POLICY.warnChars

  return (
    <div
      className="h-full flex flex-col nopan nodrag nowheel"
      onPointerDown={(e) => e.stopPropagation()}
      data-testid="sound-expert-config-tab"
    >
      <div
        className="grid grid-cols-3 gap-1 border-b border-border-subtle bg-surface-0 p-2"
        data-testid="sound-mode-tabs"
        data-nux-anchor="sound-node-purpose"
        data-nux-node-id={nodeId}
        data-nux-node-type="soundExpert"
      >
        {MODES.map((m) => {
          const Icon = MODE_ICONS[m]
          return (
            <button
              key={m}
              type="button"
              onClick={() => handleModeChange(m)}
              className={`flex h-8 min-w-0 items-center justify-center gap-1 rounded-lg border px-2 text-xs font-medium transition-colors ${
                mode === m
                  ? 'border-primary/40 bg-primary/15 text-primary'
                  : 'border-input-border bg-surface-1 text-muted-foreground hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground'
              }`}
              aria-pressed={mode === m}
              data-testid={`sound-mode-tab-${m}`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" strokeWidth={2} />
              <span className="truncate">{t(MODE_LABEL_KEYS[m])}</span>
            </button>
          )
        })}
      </div>

      {/* Prompt area -- grows to fill the fixed-height panel. This is a flex
          column so the prompt editor can own its height via `flex-1` instead of
          a content-driven cap; long prompts scroll inside the editor, not here. */}
      <div ref={promptAreaRef} className="flex-1 flex flex-col min-h-0 px-3 pt-3 pb-2">
        {mode === 'speech' ? (
          /* flexFill (not minHeight/maxHeight) — the speech editor used to be
             hard-capped at 120px inside an `h-full` wrapper, so a long dialogue
             scrolled in a sliver of the panel with dead space beneath it
             (post 1597931935297437). Same fix as ConfigTabContent's prompt box
             under post 1533007285123236. */
          <div className="flex flex-1 flex-col gap-3 min-h-0">
            <div
              className="flex min-h-[92px] flex-[0_0_42%] flex-col nodrag nowheel"
              {...LOCAL_WHEEL_TARGET_PROPS}
              data-canvas-local-wheel-node-id={nodeId}
              data-canvas-local-wheel-source-surface="speech"
              data-canvas-local-wheel-target-role="rich-text-editor"
              onWheelCapture={(event) => logPromptWheelCapture(event, 'speech')}
            >
              <CanvasRichTextBox
                flexFill
                containerClassName="flex-1 min-h-[60px] overflow-hidden bg-surface-1 border border-border-subtle rounded p-2"
                containerTestId="speech-prompt-area"
                referenceOwnerNodeId={nodeId}
                referenceOwnerNodeType="soundExpert"
                placeholder={t('soundExpertConfigTab.writeDialogue')}
                /* 0 hides RichTextBox's built-in counter: its default 1000 is not
                   a speech limit, and it only counts typed input. The counter
                   below uses SPEECH_TEXT_POLICY instead. */
                maxLength={0}
                initialContent={speechContent || undefined}
                onContentChange={onSpeechContentChange}
                onMentionClick={onMentionClick}
              />
              {/* Soft disclosure only, like the SFX counter: NOT a maxLength and
                  NOT wired to the Generate button. eleven_v3's own text_too_long
                  decides (CLAUDE.md #7). */}
              {speechTextOverLimit ? (
                <div
                  className="flex-shrink-0 pt-1 text-xs font-medium text-amber-500"
                  data-testid="speech-text-counter"
                  data-state="over"
                  role="status"
                >
                  {t('soundExpertConfigTab.sfxPromptOverLimit', {
                    count: speechTextLength,
                    max: SPEECH_TEXT_POLICY.maxChars,
                  })}
                </div>
              ) : speechTextNearLimit ? (
                <div
                  className="flex-shrink-0 pt-1 text-xs font-medium text-text-muted"
                  data-testid="speech-text-counter"
                  data-state="near"
                  role="status"
                >
                  {speechTextLength} / {SPEECH_TEXT_POLICY.maxChars}
                </div>
              ) : null}
            </div>
            <SoundExpertSpeechVoicePanel
              voiceProfile={voiceProfile}
              onVoiceProfileChange={onVoiceProfileChange}
              className="min-h-0 flex-1 overflow-hidden"
            />
          </div>
        ) : (
          /* `flex-1 min-h-0`, not `h-full`: the parent is now a flex column,
             and mixing percentage heights with flex layout is exactly what
             trapped the speech editor (RFC-1260). */
          <div className="flex flex-col flex-1 min-h-0">
            <textarea
              value={mode === 'music' ? musicPrompt : sfxPrompt}
              onChange={(e) =>
                mode === 'music'
                  ? onMusicPromptChange(e.target.value)
                  : onSfxPromptChange(e.target.value)
              }
              placeholder={PROMPT_PLACEHOLDER_KEYS[mode] ? t(PROMPT_PLACEHOLDER_KEYS[mode]) : ''}
              data-testid={`${mode}-prompt-input`}
              className="w-full flex-1 min-h-[60px] text-sm bg-surface-1 text-text-primary border border-border-subtle rounded px-2 py-1.5 resize-none focus:outline-none focus:border-primary/50 transition-colors placeholder:text-text-muted/40 nodrag nowheel"
              {...LOCAL_WHEEL_TARGET_PROPS}
              data-canvas-local-wheel-node-id={nodeId}
              data-canvas-local-wheel-source-surface={mode}
              data-canvas-local-wheel-target-role="textarea"
              onWheelCapture={(event) => logPromptWheelCapture(event, textPromptMode)}
            />
            {/* Soft disclosure only. Deliberately NOT a maxLength and NOT wired
                to the Generate button: over-limit stays submittable so the
                provider's own text_too_long is what decides (CLAUDE.md #7,
                RFC-1202 open question 1). */}
            {mode === 'sfx' && sfxPromptOverLimit ? (
              <div
                className="flex-shrink-0 pt-1 text-xs font-medium text-amber-500"
                data-testid="sfx-prompt-counter"
                role="status"
              >
                {t('soundExpertConfigTab.sfxPromptOverLimit', {
                  count: sfxPromptLength,
                  max: SFX_PROMPT_POLICY.maxChars,
                })}
              </div>
            ) : mode === 'sfx' && sfxPromptNearLimit ? (
              <div
                className="flex-shrink-0 pt-1 text-xs font-medium text-text-muted"
                data-testid="sfx-prompt-counter"
                role="status"
              >
                {sfxPromptLength} / {SFX_PROMPT_POLICY.maxChars}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Bottom toolbar -- compact single line */}
      <div className="flex-shrink-0 border-t border-border-subtle bg-surface-0 px-3 py-2">
        {/* Flyover popover -- portal-based */}
        <FlyoutPortal
          triggerRef={configChipRef}
          isOpen={isFlyoverOpen}
          onClose={closeFlyover}
        >
          <SoundConfigFlyover
            mode={mode}
            duration={duration}
            loop={loop}
            lyricsMode={lyricsMode}
            voiceProfile={voiceProfile}
            onDurationChange={onDurationChange}
            onLoopChange={onLoopChange}
            onLyricsModeChange={onLyricsModeChange}
            onVoiceProfileChange={onVoiceProfileChange}
          />
        </FlyoutPortal>

        <div className="flex items-center gap-1.5" data-testid="sound-config-toolbar">
          {/* Config summary chip -> opens flyover */}
          {mode !== 'speech' ? (
            <button
              ref={configChipRef}
              onClick={() => setIsFlyoverOpen(!isFlyoverOpen)}
              className="text-xxs px-2 py-1 rounded-full bg-surface-1 border border-border-subtle text-text-muted hover:text-foreground hover:bg-surface-2 transition-colors truncate"
              data-testid="config-summary-chip"
              title={t('soundExpertConfigTab.settingsTitle', { mode: t(MODE_LABEL_KEYS[mode]) })}
            >
              {configSummary}
            </button>
          ) : null}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Retry Count — Collapsed Pill with Popover */}
          <div data-testid="retry-selector">
            <button
              ref={retryPillRef}
              onClick={() => setIsRetryPopoverOpen(!isRetryPopoverOpen)}
              className="px-2.5 py-1 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
              data-testid="retry-pill"
            >
              {retryCount}x
            </button>

            <FlyoutPortal
              triggerRef={retryPillRef}
              isOpen={isRetryPopoverOpen}
              onClose={closeRetryPopover}
              minWidth={48}
            >
              <div className="flex flex-col gap-0.5 -m-3" data-testid="retry-popover">
                {([4, 3, 2, 1]).map((n) => (
                  <button
                    key={n}
                    onClick={() => {
                      onRetryCountChange(n)
                      setIsRetryPopoverOpen(false)
                    }}
                    className={`w-full px-3 py-1.5 text-xs font-medium text-center transition-colors ${
                      retryCount === n
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-muted hover:text-foreground hover:bg-surface-2'
                    }`}
                    data-testid={`retry-${n}x`}
                  >
                    {n}x
                  </button>
                ))}
              </div>
            </FlyoutPortal>
          </div>

          {/* Sparks Count */}
          <span className="flex items-center gap-1 text-xs font-medium text-text-muted tabular-nums" data-testid="sparks-count">
            <svg className="w-3.5 h-3.5 text-yellow-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
            </svg>
            {sparksCount}
          </span>

          {/* Generate button */}
          <button
            onClick={onGenerate}
            disabled={generateDisabled}
            className="w-8 h-8 flex items-center justify-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--primary)' }}
            title={t('soundExpertConfigTab.generate')}
            data-testid="generate-btn"
          >
            <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
})

SoundExpertConfigTab.displayName = 'SoundExpertConfigTab'

export { SoundExpertConfigTab, ACCENT as SOUND_ACCENT }
